// The moderation control area — ADR-0062, DEV_TECH_DESIGN-0062 §5.2.
//
// A bundle module rather than an inline script or a direct <script src>,
// although both patterns exist in the dashboard area already: axios's
// X-CSRFToken default header is set in main.js, so a module in the bundle
// gets FR-41 for free and no call here handles a token itself.
//
// The one rule this file keeps that no test can see: it holds no original
// question or answer in a variable beyond the lifetime of the element that
// shows it, and it never writes one to localStorage. Disclosure is recorded
// server-side; a copy that outlives the page would be a copy nobody recorded.

import { axios } from './vendor.js';

let config = {};
let clauses = {};
// The Personas tab lists open work only, unless the reviewer asks for the
// settled rows too. Kept in memory with the last fetched rows, so the toggle
// re-renders without a second request.
let objectRows = [];
let showSettled = false;

function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
}

function formatMoment(iso) {
    if (!iso) return '—';
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return escapeHtml(iso);
    return parsed.toLocaleString('de-DE', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
    });
}

// Rows arrive oldest-first and grouped only by accident of that order, so the
// grouping happens here (FR-12): a pattern across one session or one account
// is the thing a queue of single rows hides.
function groupBySubject(rows) {
    const groups = [];
    const index = new Map();
    rows.forEach((row) => {
        if (!index.has(row.subject_ref)) {
            const group = { subject_ref: row.subject_ref, tier: row.tier, rows: [] };
            index.set(row.subject_ref, group);
            groups.push(group);
        }
        index.get(row.subject_ref).rows.push(row);
    });
    return groups;
}

function clauseOptions() {
    return Object.entries(clauses)
        .map(([key, label]) => `<option value="${escapeHtml(key)}">${escapeHtml(key)} — ${escapeHtml(label)}</option>`)
        .join('');
}

// The path comes from `url_for` through a data attribute with a placeholder
// in it, rather than being spelled out here: one definition of the route,
// and a rename in Flask does not leave a dead link in a bundle.
function casePath(entryId) {
    return (config.casePath || '').replace('__ENTRY_ID__', encodeURIComponent(entryId));
}

function renderRow(row) {
    const tags = (row.tags || [])
        .map((tag) => `<span class="badge bg-light text-dark border me-1">${escapeHtml(tag)}</span>`)
        .join('');
    const thread = row.conversation_id
        ? `<span class="ms-2">Gespräch, Beitrag ${escapeHtml(row.turn_index)}</span>`
        : '';
    const openCase = config.casePath
        ? `<a class="btn btn-sm btn-outline-secondary" href="${escapeHtml(casePath(row.entry_id))}">
               <i class="fas fa-comments fa-fw me-1"></i>Im Gespräch ansehen
           </a>`
        : '';
    return `
    <div class="case-row" data-entry-id="${escapeHtml(row.entry_id)}">
        <div class="case-query">${escapeHtml(row.generic_query) || '<span class="text-muted">(keine Abstraktion)</span>'}</div>
        <div class="case-meta">
            ${formatMoment(row.created_at)}
            · ${escapeHtml(row.domain) || 'ohne Bereich'}
            · ${escapeHtml(row.persona_name)}
            · ${escapeHtml(row.model_name)}
            ${thread}
        </div>
        <div class="case-meta">Ziel: ${escapeHtml(row.main_goal) || '—'}</div>
        <div class="case-tags mt-1">${tags}</div>
        <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
            <select class="form-select form-select-sm w-auto" data-role="verdict">
                <option value="unauffaellig">unauffällig</option>
                <option value="grenzfall">Grenzfall</option>
                <option value="verstoss">Verstoß</option>
            </select>
            <select class="form-select form-select-sm w-auto" data-role="clause" hidden>
                <option value="">Klausel wählen …</option>
                ${clauseOptions()}
            </select>
            <input type="text" class="form-control form-control-sm" style="max-width: 22rem"
                   data-role="rationale" placeholder="Begründung — nie den geprüften Text zitieren">
            <button type="button" class="btn btn-sm btn-primary" data-role="submit-verdict">Urteil festhalten</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" data-role="disclose">
                <i class="fas fa-unlock fa-fw me-1"></i>Original öffnen
            </button>
            ${openCase}
        </div>
        <div class="case-meta text-danger mt-1" data-role="row-status" hidden></div>
        <div class="original-text" data-role="original" hidden></div>
    </div>`;
}

function renderQueue(rows) {
    const container = document.getElementById('controlQueue');
    const status = document.getElementById('controlQueueStatus');
    if (!rows.length) {
        container.innerHTML = '';
        status.textContent = 'Keine offenen Vorgänge.';
        return;
    }
    status.textContent = `${rows.length} offene Vorgänge, älteste zuerst.`;
    container.innerHTML = groupBySubject(rows).map((group) => `
        <div class="subject-group">
            <div class="subject-ref mb-1">
                <i class="fas fa-user fa-fw me-1"></i>${escapeHtml(group.subject_ref)}
                <span class="badge bg-secondary ms-2">${escapeHtml(group.tier)}</span>
                <span class="text-muted ms-2">${group.rows.length} Vorgang/Vorgänge</span>
            </div>
            ${group.rows.map(renderRow).join('')}
        </div>`).join('');
}

async function loadQueue() {
    const status = document.getElementById('controlQueueStatus');
    try {
        const response = await axios.get(config.queueUrl);
        renderQueue(response.data.rows || []);
    } catch (error) {
        status.textContent = 'Die Warteschlange konnte nicht geladen werden.';
    }
}

function showRowError(row, message) {
    const line = row.querySelector('[data-role="row-status"]');
    line.textContent = message;
    line.hidden = false;
}

async function submitVerdict(row) {
    const verdict = row.querySelector('[data-role="verdict"]').value;
    const clause = row.querySelector('[data-role="clause"]').value;
    const rationale = row.querySelector('[data-role="rationale"]').value;
    try {
        await axios.post(config.verdictUrl, {
            object_type: 'answer',
            object_ref: row.dataset.entryId,
            verdict,
            clause: clause || null,
            rationale,
            scope: 'turn'
        });
        row.remove();
    } catch (error) {
        const message = error.response && error.response.data && error.response.data.error;
        showRowError(row, message || 'Das Urteil konnte nicht festgehalten werden.');
    }
}

// --- one case, in its thread ----------------------------------------------

function caseTurnBadges(turn) {
    const badges = [];
    if (turn.is_focus) badges.push('<span class="badge bg-primary ms-2">dieser Vorgang</span>');
    if (turn.reviewed) badges.push('<span class="badge bg-success ms-2">bereits geprüft</span>');
    if (turn.before_cutover) badges.push('<span class="badge bg-secondary ms-2">vor Inbetriebnahme</span>');
    return badges.join('');
}

function renderCaseTurn(turn) {
    const row = turn.row;
    const tags = (row.tags || [])
        .map((tag) => `<span class="badge bg-light text-dark border me-1">${escapeHtml(tag)}</span>`)
        .join('');
    return `
    <div class="case-row${turn.is_focus ? ' is-focus' : ''}" data-entry-id="${escapeHtml(row.entry_id)}">
        <div class="case-query">
            ${row.turn_index == null ? '' : `<span class="text-muted me-2">Beitrag ${escapeHtml(row.turn_index)}</span>`}
            ${escapeHtml(row.generic_query) || '<span class="text-muted">(keine Abstraktion)</span>'}
            ${caseTurnBadges(turn)}
        </div>
        <div class="case-meta">
            ${formatMoment(row.created_at)}
            · ${escapeHtml(row.domain) || 'ohne Bereich'}
            · ${escapeHtml(row.persona_name)}
            · ${escapeHtml(row.model_name)}
        </div>
        <div class="case-meta">Ziel: ${escapeHtml(row.main_goal) || '—'}</div>
        <div class="case-tags mt-1">${tags}</div>
        <div class="mt-2">
            <button type="button" class="btn btn-sm btn-outline-secondary" data-role="disclose">
                <i class="fas fa-unlock fa-fw me-1"></i>Original öffnen
            </button>
        </div>
        <div class="case-meta text-danger mt-1" data-role="row-status" hidden></div>
        <div class="original-text" data-role="original" hidden></div>
    </div>`;
}

// One form for the whole case rather than one per turn. FR-15: a verdict may
// be recorded against the conversation, and then one document is written per
// turn still in the queue, each carrying the same verdict and the shared
// conversation reference — the record stays per case so the queue arithmetic
// has one meaning, and `conversation_ref` is what says the judgement was made
// once.
function renderCaseVerdictForm(view) {
    const threadOption = view.conversation_id
        ? `<option value="thread">das ganze Gespräch (${view.queued_turns} offene Beiträge)</option>`
        : '';
    return `
    <div class="case-verdict border-top pt-3 mt-3">
        <div class="d-flex flex-wrap align-items-center gap-2">
            <select class="form-select form-select-sm w-auto" data-role="scope">
                <option value="turn">nur diesen Vorgang</option>
                ${threadOption}
            </select>
            <select class="form-select form-select-sm w-auto" data-role="verdict">
                <option value="unauffaellig">unauffällig</option>
                <option value="grenzfall">Grenzfall</option>
                <option value="verstoss">Verstoß</option>
            </select>
            <select class="form-select form-select-sm w-auto" data-role="clause" hidden>
                <option value="">Klausel wählen …</option>
                ${clauseOptions()}
            </select>
            <input type="text" class="form-control form-control-sm" style="max-width: 22rem"
                   data-role="rationale" placeholder="Begründung — nie den geprüften Text zitieren">
            <button type="button" class="btn btn-sm btn-primary" data-role="submit-case-verdict">Urteil festhalten</button>
        </div>
        <div class="case-meta text-danger mt-1" data-role="row-status" hidden></div>
    </div>`;
}

function renderCase(view) {
    const status = document.getElementById('controlCaseStatus');
    const header = document.getElementById('controlCaseHeader');
    const container = document.getElementById('controlCase');
    const turns = view.turns || [];
    status.textContent = view.conversation_id
        ? `${turns.length} Beiträge in diesem Gespräch, davon ${view.reviewed_turns} bereits geprüft.`
        : 'Einzelne Antwort, kein Gesprächszusammenhang.';
    header.innerHTML = view.conversation_id
        ? `<div class="subject-ref">Gespräch ${escapeHtml(view.conversation_id)}</div>`
        : '';
    container.innerHTML = turns.map(renderCaseTurn).join('') + renderCaseVerdictForm(view);
}

async function loadCase() {
    const status = document.getElementById('controlCaseStatus');
    try {
        const response = await axios.get(config.caseUrl);
        renderCase(response.data);
    } catch (error) {
        status.textContent = 'Der Vorgang konnte nicht geladen werden.';
    }
}

async function submitCaseVerdict(form) {
    const scope = form.querySelector('[data-role="scope"]').value;
    const verdict = form.querySelector('[data-role="verdict"]').value;
    const clause = form.querySelector('[data-role="clause"]').value;
    const rationale = form.querySelector('[data-role="rationale"]').value;
    try {
        const response = await axios.post(config.verdictUrl, {
            object_type: 'answer',
            object_ref: config.entryId,
            verdict,
            clause: clause || null,
            rationale,
            scope
        });
        // The turns stay on screen after the verdict. They are no longer work
        // to be done, but the reviewer may still want to read what they just
        // judged, and nothing here is destroyed by looking at it again.
        form.outerHTML = `<div class="case-meta border-top pt-3 mt-3">Festgehalten: ${escapeHtml(response.data.written)} Eintrag/Einträge im Protokoll.</div>`;
    } catch (error) {
        const message = error.response && error.response.data && error.response.data.error;
        showRowError(form, message || 'Das Urteil konnte nicht festgehalten werden.');
    }
}

// --- standing objects: personas and facets --------------------------------

function reviewState(row) {
    if (row.blocked) return '<span class="badge bg-danger">stillgelegt</span>';
    if (!row.reviewed) return '<span class="badge bg-warning text-dark">ungeprüft</span>';
    if (row.stale) return '<span class="badge bg-warning text-dark">nach Prüfung geändert</span>';
    return '<span class="badge bg-success">geprüft</span>';
}

function renderObjectRow(row) {
    const visibility = row.object_type === 'facet'
        ? `<span class="badge bg-light text-dark border ms-2">${row.is_public ? 'öffentlich' : 'privat'}</span>`
        : '';
    return `
    <div class="object-row" data-object-type="${escapeHtml(row.object_type)}" data-object-ref="${escapeHtml(row.object_ref)}">
        <div class="d-flex flex-wrap align-items-center gap-2">
            <span class="object-name">${escapeHtml(row.name)}</span>
            ${reviewState(row)}
            ${visibility}
        </div>
        <div class="object-meta">${escapeHtml(row.owner_ref)}${row.created_at ? ' · ' + formatMoment(row.created_at) : ''}</div>
        <div class="object-text">${escapeHtml(row.text) || '<span class="text-muted">(kein Text)</span>'}</div>
        <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
            <select class="form-select form-select-sm w-auto" data-role="verdict">
                <option value="unauffaellig">unauffällig</option>
                <option value="grenzfall">Grenzfall</option>
                <option value="verstoss">Verstoß</option>
            </select>
            <select class="form-select form-select-sm w-auto" data-role="clause" hidden>
                <option value="">Klausel wählen …</option>
                ${clauseOptions()}
            </select>
            <input type="text" class="form-control form-control-sm" style="max-width: 22rem"
                   data-role="rationale" placeholder="Begründung — nie den geprüften Text zitieren">
            <button type="button" class="btn btn-sm btn-primary" data-role="submit-object-verdict">Urteil festhalten</button>
            <button type="button" class="btn btn-sm ${row.blocked ? 'btn-outline-success' : 'btn-outline-danger'}" data-role="toggle-block">
                ${row.blocked ? 'Wieder freigeben' : 'Stilllegen'}
            </button>
        </div>
        <div class="object-meta text-danger mt-1" data-role="row-status" hidden></div>
        <div class="object-meta mt-2" data-role="carriers" hidden></div>
    </div>`;
}

// Open is what still needs a verdict: never judged, or judged and then edited.
// A settled row leaves the list the moment its verdict is recorded; an edit
// afterwards brings it back as "nach Prüfung geändert".
function isOpen(row) {
    return !row.reviewed || row.stale;
}

function renderObjectSection(heading, rows) {
    const visible = showSettled ? rows : rows.filter(isOpen);
    const empty = rows.length ? 'Nichts offen.' : 'Keine.';
    return `
        <div class="section-heading">${heading}</div>
        ${visible.map(renderObjectRow).join('') || `<div class="text-muted small">${empty}</div>`}`;
}

function renderObjects(rows) {
    const container = document.getElementById('controlObjects');
    const status = document.getElementById('controlObjectsStatus');
    const personas = rows.filter((row) => row.object_type === 'persona');
    const facets = rows.filter((row) => row.object_type === 'facet');
    const open = rows.filter(isOpen).length;
    status.textContent = `${personas.length} Personas, ${facets.length} Ausprägungen — davon ${open} offen.`;
    container.innerHTML = `
        <div class="form-check form-switch mt-2">
            <input class="form-check-input" type="checkbox" id="controlShowSettled"
                   data-role="show-settled" ${showSettled ? 'checked' : ''}>
            <label class="form-check-label small" for="controlShowSettled">Erledigte anzeigen (${rows.length - open})</label>
        </div>
        ${renderObjectSection('Personas', personas)}
        ${renderObjectSection('Ausprägungen', facets)}`;
}

async function loadObjects() {
    const status = document.getElementById('controlObjectsStatus');
    try {
        const response = await axios.get(config.objectsUrl);
        objectRows = response.data.rows || [];
        renderObjects(objectRows);
    } catch (error) {
        status.textContent = 'Die Objekte konnten nicht geladen werden.';
    }
}

async function submitObjectVerdict(row) {
    const verdict = row.querySelector('[data-role="verdict"]').value;
    const clause = row.querySelector('[data-role="clause"]').value;
    const rationale = row.querySelector('[data-role="rationale"]').value;
    try {
        await axios.post(config.verdictUrl, {
            object_type: row.dataset.objectType,
            object_ref: row.dataset.objectRef,
            verdict,
            clause: clause || null,
            rationale
        });
        loadObjects();
    } catch (error) {
        const message = error.response && error.response.data && error.response.data.error;
        showRowError(row, message || 'Das Urteil konnte nicht festgehalten werden.');
    }
}

async function toggleBlock(row) {
    const rationale = row.querySelector('[data-role="rationale"]').value;
    const blocked = row.querySelector('[data-role="toggle-block"]').textContent.trim() === 'Stilllegen';
    try {
        const response = await axios.post(config.blockUrl, {
            object_type: row.dataset.objectType,
            object_ref: row.dataset.objectRef,
            blocked,
            rationale
        });
        // The facet-to-persona gap, stated in the interface rather than hidden
        // in the documentation: a facet's text is copied into a persona at
        // composition time, so blocking the facet does nothing to the personas
        // that already carry it. These are the ones to block in the same
        // sitting.
        const carriers = response.data.personas_carrying_the_text || [];
        if (carriers.length) {
            const target = row.querySelector('[data-role="carriers"]');
            target.innerHTML = `<strong>Diese Personas enthalten den Text dieser Ausprägung:</strong> ${carriers.map(escapeHtml).join(', ')}. Die Stilllegung der Ausprägung erreicht sie nicht — sie muss für jede einzeln erfolgen.`;
            target.hidden = false;
            row.querySelector('[data-role="toggle-block"]').textContent = 'Wieder freigeben';
            return;
        }
        loadObjects();
    } catch (error) {
        const message = error.response && error.response.data && error.response.data.error;
        showRowError(row, message || 'Die Stilllegung konnte nicht ausgeführt werden.');
    }
}

// --- the record -----------------------------------------------------------

// German verbs rather than the stored keys. The record is read by whoever has
// to answer "do you check?", and `deactivated` is not an answer to that.
const ACTION_LABELS = {
    reviewed: 'geprüft',
    disclosed: 'Original geöffnet',
    deactivated: 'stillgelegt',
    reactivated: 'wieder freigegeben'
};

const VERDICT_LABELS = {
    unauffaellig: 'unauffällig',
    grenzfall: 'Grenzfall',
    verstoss: 'Verstoß'
};

const OBJECT_LABELS = {
    answer: 'Antwort',
    persona: 'Persona',
    facet: 'Ausprägung'
};

function verdictBadge(row) {
    if (!row.verdict) return '';
    const tone = row.verdict === 'verstoss'
        ? 'bg-danger'
        : (row.verdict === 'grenzfall' ? 'bg-warning text-dark' : 'bg-success');
    return `<span class="badge ${tone} ms-2">${escapeHtml(VERDICT_LABELS[row.verdict] || row.verdict)}</span>`;
}

function renderLogRow(row) {
    const clause = row.clause
        ? `<span class="ms-2">${escapeHtml(row.clause)}${row.clause_label ? ' — ' + escapeHtml(row.clause_label) : ''}</span>`
        : '';
    const thread = row.conversation_ref
        ? `<div class="log-meta">Im Gesprächszusammenhang beurteilt: <span class="log-ref">${escapeHtml(row.conversation_ref)}</span></div>`
        : '';
    return `
    <div class="log-row">
        <div>
            <strong>${escapeHtml(ACTION_LABELS[row.action] || row.action)}</strong>
            <span class="text-muted">· ${escapeHtml(OBJECT_LABELS[row.object_type] || row.object_type)}</span>
            ${verdictBadge(row)}
            ${clause}
        </div>
        <div class="log-meta">
            ${formatMoment(row.created_at)}
            · Objekt <span class="log-ref">${escapeHtml(row.object_ref)}</span>
            · Betroffen: <span class="log-ref">${escapeHtml(row.subject_ref) || '—'}</span>
            · Prüferin/Prüfer <span class="log-ref">${escapeHtml(row.reviewer_id)}</span>
        </div>
        ${thread}
        ${row.rationale ? `<div class="log-rationale">${escapeHtml(row.rationale)}</div>` : ''}
    </div>`;
}

async function loadLog() {
    const status = document.getElementById('controlLogStatus');
    try {
        const response = await axios.get(config.logUrl);
        const rows = response.data.rows || [];
        const container = document.getElementById('controlLog');
        if (!rows.length) {
            container.innerHTML = '';
            status.textContent = 'Noch keine Einträge.';
            return;
        }
        status.textContent = `${rows.length} Einträge, neueste zuerst.`;
        container.innerHTML = rows.map(renderLogRow).join('');
    } catch (error) {
        status.textContent = 'Das Protokoll konnte nicht geladen werden.';
    }
}

// The original is written straight into the element that shows it and is held
// nowhere else — no module variable, no localStorage. A copy that outlives the
// page would be a copy nobody recorded.
async function discloseOriginal(row, trigger) {
    const target = row.querySelector('[data-role="original"]');
    trigger.disabled = true;
    try {
        const response = await axios.post(config.discloseUrl, {
            entry_id: row.dataset.entryId
        });
        target.innerHTML = `
            <div class="fw-semibold mb-1">Frage</div>
            <div class="mb-2">${escapeHtml(response.data.question)}</div>
            <div class="fw-semibold mb-1">Antwort</div>
            <div>${escapeHtml(response.data.reply)}</div>`;
        target.hidden = false;
        trigger.remove();
    } catch (error) {
        trigger.disabled = false;
        const message = error.response && error.response.data && error.response.data.error;
        showRowError(row, message || 'Das Original konnte nicht geöffnet werden.');
    }
}

function onAreaChange(event) {
    if (event.target.dataset.role === 'show-settled') {
        showSettled = event.target.checked;
        renderObjects(objectRows);
        return;
    }
    if (event.target.dataset.role !== 'verdict') return;
    const row = event.target.closest('.case-row, .object-row, .case-verdict');
    // A clause is only meaningful on a Verstoß, and only then is it required.
    row.querySelector('[data-role="clause"]').hidden = event.target.value !== 'verstoss';
}

function onAreaClick(event) {
    const verdictTrigger = event.target.closest('[data-role="submit-verdict"]');
    if (verdictTrigger) {
        submitVerdict(verdictTrigger.closest('.case-row'));
        return;
    }
    const discloseTrigger = event.target.closest('[data-role="disclose"]');
    if (discloseTrigger) {
        discloseOriginal(discloseTrigger.closest('.case-row'), discloseTrigger);
        return;
    }
    const objectTrigger = event.target.closest('[data-role="submit-object-verdict"]');
    if (objectTrigger) {
        submitObjectVerdict(objectTrigger.closest('.object-row'));
        return;
    }
    const blockTrigger = event.target.closest('[data-role="toggle-block"]');
    if (blockTrigger) {
        toggleBlock(blockTrigger.closest('.object-row'));
        return;
    }
    const caseTrigger = event.target.closest('[data-role="submit-case-verdict"]');
    if (caseTrigger) {
        submitCaseVerdict(caseTrigger.closest('.case-verdict'));
    }
}

export function initializeControlArea() {
    const area = document.getElementById('controlArea');
    if (!area) return;
    config = {
        queueUrl: area.dataset.queueUrl,
        verdictUrl: area.dataset.verdictUrl,
        discloseUrl: area.dataset.discloseUrl,
        objectsUrl: area.dataset.objectsUrl,
        blockUrl: area.dataset.blockUrl,
        logUrl: area.dataset.logUrl,
        caseUrl: area.dataset.caseUrl,
        casePath: area.dataset.casePath,
        entryId: area.dataset.entryId
    };
    const clauseData = document.getElementById('controlClauses');
    if (clauseData) {
        try {
            clauses = JSON.parse(clauseData.textContent);
        } catch (error) {
            clauses = {};
        }
    }
    area.addEventListener('change', onAreaChange);
    area.addEventListener('click', onAreaClick);
    if (area.dataset.area === 'queue') {
        loadQueue();
    }
    if (area.dataset.area === 'objects') {
        loadObjects();
    }
    if (area.dataset.area === 'log') {
        loadLog();
    }
    if (area.dataset.area === 'case') {
        loadCase();
    }
}
